import { useEffect, useRef, useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import type { FormulatorSlotGuide, FormulatorToolsetBridge } from "@ttc-spectree/toolbox-contract";
import { FormulatorNotice } from "@/components/formulator/formulator-notice";
import type { InterviewView } from "@/lib/api/work-item-interview";
import {
  formulatorErrorMessage,
  formulatorOpeningTurnMessage,
  type FormulatorErrorMessage,
  type FormulatorTurnState,
} from "@/lib/toolbox/formulator";
import { cn } from "@/lib/utils";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";

/**
 * The Toolbox Formulator's interview view (feature PI-SPEC-SW-1418; design
 * Write view `poInterview`): the conversation with Claude on the left, the
 * live Draft against the grooming bar and What Claude found on the right.
 *
 * Replies go through `formulator.reply` (the turn runs on the person's
 * Claude seat); the page shows the typing indicator with the engine's latest
 * activity line until the turn finishes, then the refetched session brings
 * the next question, quick picks, Draft and findings. A failed turn posts
 * nothing, so the typed reply goes back into the input; when sending it again
 * is the fix (NO_TURN_RETURNED), the notice offers Try again.
 */

const KIND_LABELS: Record<InterviewView["kind"], string> = { story: "User story", bug: "Bug" };

/** How long a just-filled Draft row stays highlighted. */
const FRESH_MS = 1600;

/** The most duplicate candidates shown (the contract's limit). */
const MAX_DUPLICATES = 5;

/** The neutral line of a missing slot when SpecTree sends no `hint` (an older SpecTree). */
export const SLOT_NOT_CAPTURED = "Not captured yet";

/**
 * Whether a Draft row shows a placeholder rather than captured values: the
 * slot is not in place and has parts still missing. Such a line is styled
 * apart (italic, faint grey) so a hint never reads as an answer. Pure;
 * exported for tests.
 */
export function slotShowsHint(slot: FormulatorSlotGuide): boolean {
  return !slot.filled && slot.parts.some((part) => (part.value?.trim() ?? "") === "");
}

/**
 * What a Draft row shows under its label: the current values once the slot is
 * in place, otherwise the person-facing hints of the parts still missing
 * (UX-PM-24), joined with "; ". `part.description` is Claude's extraction
 * instruction and is never shown; an older SpecTree that sends no `hint`
 * gets the neutral {@link SLOT_NOT_CAPTURED} line rather than that text.
 * Pure; exported for tests.
 */
export function slotLine(slot: FormulatorSlotGuide): string {
  const values = slot.parts.map((part) => part.value?.trim() ?? "").filter((value) => value !== "");
  if (!slotShowsHint(slot)) return values.join(" · ");
  const hints = slot.parts
    .filter((part) => (part.value?.trim() ?? "") === "")
    .map((part) => part.hint?.trim() ?? "")
    .filter((hint) => hint !== "");
  return hints.length > 0 ? hints.join("; ") : SLOT_NOT_CAPTURED;
}

/** A list marker Claude or the person may have typed ("1.", "2)", "Step 3:", "-", "*", "•"); the Draft numbers steps itself. */
const LIST_MARKER = /^\s*(?:\d+[.)]|step\s*\d+[:.)]?|[-*•])\s*/i;

/** A part's value split at its line breaks, blank lines dropped. */
function valueLines(value: string | null | undefined): string[] {
  return (value ?? "")
    .split(/\r\n?|\n/)
    .map((line) => line.trim())
    .filter((line) => line !== "");
}

/**
 * The lines a Draft row shows for its captured values (UX-PM-44). A value
 * with no line break renders exactly as {@link slotLine} does (one line,
 * parts joined with " · "). Once any part's value holds line breaks, it is
 * split within each part only: every line of a part is its own line, and
 * each part after the first starts a new line with the same " · " mark
 * (`· It jumps`), so the boundary between parts stays visible. The repro
 * steps are numbered ("1. Open the Controllers list") the way ADO will show
 * them. Hint rows are not passed through here. Pure; exported for tests.
 */
export function slotLines(slot: FormulatorSlotGuide): string[] {
  if (!slot.parts.some((part) => /\r|\n/.test(part.value?.trim() ?? ""))) return [slotLine(slot)];
  const parts = slot.parts.map((part) => valueLines(part.value)).filter((lines) => lines.length > 0);
  const numbered =
    slot.id === "reproSteps"
      ? parts.map((lines) =>
          lines
            .map((line) => line.replace(LIST_MARKER, "").trim())
            .filter((line) => line !== "")
            .map((line, index) => `${String(index + 1)}. ${line}`)
        )
      : parts;
  return numbered
    .filter((lines) => lines.length > 0)
    .flatMap((lines, partIndex) => (partIndex === 0 ? lines : lines.map((line, index) => (index === 0 ? `· ${line}` : line))));
}

/** "<filled> of <targets> in place" counts the rubric's target slots, never the optional ones. Pure; exported for tests. */
export function draftProgress(slotGuide: readonly FormulatorSlotGuide[]): { filled: number; targets: number } {
  const targets = slotGuide.filter((slot) => !slot.optional);
  return { filled: targets.filter((slot) => slot.filled).length, targets: targets.length };
}

function TypingDots() {
  return (
    <span className="inline-flex gap-1" aria-hidden="true">
      {[0, 150, 300].map((delay) => (
        <span
          key={delay}
          className="h-1.5 w-1.5 animate-pulse rounded-full bg-muted-foreground"
          style={{ animationDelay: `${String(delay)}ms` }}
        />
      ))}
    </span>
  );
}

function Message({ role, children }: { role: "assistant" | "user"; children: string }) {
  const ai = role === "assistant";
  return (
    <div
      className={cn(
        "max-w-[540px] rounded-xl px-3.5 py-[11px] leading-[1.55]",
        ai ? "self-start border border-[#1F1F23] bg-[#111113]" : "self-end bg-[#1F1F23]"
      )}
      data-testid="formulator-message"
      data-role={role}
    >
      <div className={cn("mb-1 text-[11px] font-semibold", TOOLBOX_TEXT.faint)}>{ai ? "Claude" : "You"}</div>
      <p className="whitespace-pre-wrap">{children}</p>
    </div>
  );
}

function DraftCard({ slotGuide }: { slotGuide: FormulatorSlotGuide[] }) {
  const { filled, targets } = draftProgress(slotGuide);
  const percent = targets > 0 ? Math.round((filled / targets) * 100) : 100;
  const previous = useRef<Set<string> | null>(null);
  const [fresh, setFresh] = useState<ReadonlySet<string>>(new Set());

  // Highlight the rows that just filled (never on first render).
  useEffect(() => {
    const now = new Set(slotGuide.filter((slot) => slot.filled).map((slot) => slot.id));
    const before = previous.current;
    previous.current = now;
    if (before === null) return undefined;
    const added = [...now].filter((id) => !before.has(id));
    if (added.length === 0) return undefined;
    setFresh(new Set(added));
    const timer = setTimeout(() => {
      setFresh(new Set());
    }, FRESH_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [slotGuide]);

  return (
    <div className="glass-card rounded-[10px]" data-testid="formulator-draft">
      <div className="flex flex-col gap-1.5 border-b border-[#1F1F23] px-3 pb-2.5 pt-3">
        <div className="flex items-center gap-2">
          <h2 className="flex-1 font-display text-base font-semibold">Draft</h2>
          <span className="text-muted-foreground" data-testid="formulator-draft-count">
            {filled} of {targets} in place
          </span>
        </div>
        <div
          className="h-1 overflow-hidden rounded-full bg-[#1F1F23]"
          role="progressbar"
          aria-label="Draft progress against the grooming bar"
          aria-valuemin={0}
          aria-valuemax={targets}
          aria-valuenow={filled}
        >
          <div
            className="h-full rounded-full bg-[#22C55E] transition-[width] duration-500"
            style={{ width: `${String(percent)}%` }}
          />
        </div>
      </div>
      {slotGuide.map((slot) => (
        <div
          key={slot.id}
          className={cn(
            "flex items-start gap-2.5 border-b border-[#1A1A1E] px-3 py-[9px] transition-colors duration-1000 last:border-b-0",
            fresh.has(slot.id) && "bg-sky-500/[0.15] duration-0"
          )}
          data-testid="formulator-slot"
          data-slot={slot.id}
          data-filled={slot.filled}
          data-fresh={fresh.has(slot.id) || undefined}
        >
          <span
            className={cn(
              "mt-px flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[9px] font-bold",
              slot.filled ? "bg-green-500/15 text-green-400" : "border border-dashed border-[#52525B]"
            )}
            aria-label={slot.filled ? "In place" : "Missing"}
          >
            {slot.filled && <Check className="h-2.5 w-2.5" aria-hidden="true" />}
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="font-medium">
              {slot.label}
              {slot.optional && <span className={cn("font-normal", TOOLBOX_TEXT.faint)}> (optional)</span>}
            </span>
            <span
              className={cn(
                "break-words text-[13px] leading-relaxed",
                slotShowsHint(slot) ? cn("italic", TOOLBOX_TEXT.faint) : "text-muted-foreground"
              )}
              data-testid="formulator-slot-line"
              data-hint={slotShowsHint(slot) || undefined}
            >
              {slotShowsHint(slot)
                ? slotLine(slot)
                : slotLines(slot).map((line, index) => (
                    <span key={`${String(index)}-${line}`} className="block" data-testid="formulator-slot-value-line">
                      {line}
                    </span>
                  ))}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function FindingsCard({ view }: { view: InterviewView }) {
  return (
    <div className="glass-card rounded-[10px]" data-testid="formulator-findings">
      <div className="border-b border-[#1F1F23] px-3 py-2.5">
        <h2 className="font-display text-[13px] font-semibold text-[#E4E4E7]">What Claude found</h2>
      </div>
      {view.findings.length === 0 ? (
        <p className="px-3 py-2.5 text-[13px] text-muted-foreground">
          Screens, settings and behaviour Claude finds in the code show up here.
        </p>
      ) : (
        view.findings.map((finding, index) => (
          <div
            key={`${finding.name}-${String(index)}`}
            className="flex items-start gap-2.5 border-b border-[#1A1A1E] px-3 py-[9px] last:border-b-0"
            data-testid="formulator-finding"
          >
            <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#23A4FF]" aria-hidden="true" />
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="font-medium">{finding.name}</span>
              <span className="text-[13px] leading-relaxed text-muted-foreground">{finding.why}</span>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function DuplicatesPanel({
  view,
  onImprove,
  onContinue,
}: {
  view: InterviewView;
  onImprove: (itemId: string) => void;
  onContinue: () => void;
}) {
  return (
    <div className="glass-card flex flex-col rounded-[10px]" data-testid="formulator-duplicates">
      <div className="flex flex-col gap-0.5 border-b border-[#1F1F23] px-4 py-3">
        <h2 className="font-display text-sm font-semibold">Is it one of these?</h2>
        <span className="text-[13px] text-muted-foreground">
          These open items in {view.container} may already describe the same thing. Improving one keeps the board tidy.
        </span>
      </div>
      {view.duplicates.slice(0, MAX_DUPLICATES).map((item) => (
        <div
          key={item.itemId}
          className="flex items-center gap-3 border-b border-[#141417] px-4 py-2.5"
          data-testid="formulator-duplicate"
        >
          <span className="font-mono text-xs text-muted-foreground">#{item.itemId}</span>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="truncate font-medium">{item.title}</span>
            <span className="text-[13px] text-muted-foreground">{item.reason}</span>
          </div>
          <button
            type="button"
            className="shrink-0 text-[13px] font-medium text-link hover:underline"
            onClick={() => {
              onImprove(item.itemId);
            }}
            data-testid="formulator-duplicate-improve"
          >
            Improve this one instead
          </button>
        </div>
      ))}
      <div className="flex justify-end px-4 py-2.5">
        <button
          type="button"
          className="inline-flex h-[30px] items-center rounded-lg border border-[#27272A] bg-[#1A1A1E] px-[11px] text-[13px] font-medium hover:bg-[#222227]"
          onClick={onContinue}
          data-testid="formulator-duplicate-continue"
        >
          Continue with a new item
        </button>
      </div>
    </div>
  );
}

export interface FormulatorInterviewProps {
  formulator: FormulatorToolsetBridge;
  view: InterviewView;
  /** The current turn from the engine's events. */
  turn: FormulatorTurnState;
  /** Shown while Claude works and the engine has sent no activity line yet. */
  activityFallback: string;
  /** Running the turn a session awaits from Claude failed on this machine. */
  resumeFailure: FormulatorErrorMessage | null;
  /** Run the turn the session awaits from Claude again. */
  onRetryClaude: () => void;
  /** Submit was refused because the draft is below the grooming bar. */
  belowBar: boolean;
  onReview: () => void;
  /** Leave this session and improve an existing item instead. */
  onImproveInstead: (itemId: string) => void;
}

export function FormulatorInterview({
  formulator,
  view,
  turn,
  activityFallback,
  resumeFailure,
  onRetryClaude,
  belowBar,
  onReview,
  onImproveInstead,
}: FormulatorInterviewProps) {
  const [input, setInput] = useState("");
  /** The reply on its way: shown as the person's message until the turn ends. */
  const [pending, setPending] = useState<{ text: string; base: number } | null>(null);
  const [failure, setFailure] = useState<FormulatorErrorMessage | null>(null);
  /** The reply whose turn failed, which Try again sends again. */
  const [retryText, setRetryText] = useState<string | null>(null);
  const [duplicatesDismissed, setDuplicatesDismissed] = useState(false);
  const messagesRef = useRef<HTMLDivElement>(null);
  const handledFinish = useRef(turn.lastFinished);
  const pendingRef = useRef(pending);
  pendingRef.current = pending;

  const messageCount = view.messages.length;

  // The refetched session carries the reply: drop the local copy.
  useEffect(() => {
    if (pending && messageCount > pending.base) setPending(null);
  }, [messageCount, pending]);

  // A failed turn posted nothing: put the reply back in the input.
  useEffect(() => {
    const finished = turn.lastFinished;
    if (!finished || finished === handledFinish.current) return;
    handledFinish.current = finished;
    if (finished.outcome !== "failed") return;
    setFailure(formulatorErrorMessage(finished.errorCode, finished.message));
    const current = pendingRef.current;
    if (current) {
      setInput((typed) => (typed.trim() === "" ? current.text : typed));
      setRetryText(current.text);
      setPending(null);
    }
  }, [turn.lastFinished]);

  // A new turn is running: the last failure no longer applies.
  useEffect(() => {
    if (!turn.running) return;
    setFailure(null);
    setRetryText(null);
  }, [turn.running]);

  useEffect(() => {
    const area = messagesRef.current;
    if (area) area.scrollTop = area.scrollHeight;
  }, [messageCount, pending, turn.running]);

  const awaitingClaude = view.awaiting === "claude";
  const busy = pending !== null || awaitingClaude || turn.running;
  const assistantCount = view.messages.filter((message) => message.role === "assistant").length;
  const questionNumber = Math.max(1, assistantCount + (busy && !view.complete ? 1 : 0));
  const subtitle = view.complete
    ? `${KIND_LABELS[view.kind]} · draft complete`
    : `${KIND_LABELS[view.kind]} · question ${String(questionNumber)}`;
  const showDuplicates =
    view.mode === "create" && view.duplicates.length > 0 && !duplicatesDismissed && !awaitingClaude;
  const quickPicks = busy ? [] : view.quickPicks;
  const canType = view.status === "active" && !busy;

  /** Send a reply. `retry`: Try again on the kept reply, which leaves an input the person has since changed as typed. */
  const send = (raw: string, retry = false) => {
    const text = raw.trim();
    if (text === "" || !canType) return;
    setFailure(null);
    setRetryText(null);
    setPending({ text, base: messageCount });
    setInput((typed) => (retry && typed.trim() !== text ? typed : ""));
    const failed = (message: FormulatorErrorMessage) => {
      setFailure(message);
      setRetryText(text);
      setPending(null);
      setInput((typed) => (typed.trim() === "" ? text : typed));
    };
    void formulator
      .reply(view.sessionId, text)
      .then((result) => {
        if (result.ok) return;
        failed(formulatorErrorMessage(result.errorCode, result.message));
      })
      .catch(() => {
        failed(formulatorErrorMessage("UNKNOWN"));
      });
  };

  // A failed turn the session awaits from Claude. On the opening turn (no question asked yet) the person has
  // typed no answer, so NO_TURN_RETURNED says "first question" and not "Your answer is kept".
  const awaitingFailure = awaitingClaude && !turn.running ? (resumeFailure ?? failure) : null;
  const claudeFailure =
    awaitingFailure && assistantCount === 0 ? formulatorOpeningTurnMessage(awaitingFailure) : awaitingFailure;
  const replyFailure = claudeFailure ? null : failure;

  return (
    <div className="flex min-h-0 flex-1 border-t border-[#1F1F23]" data-testid="formulator-interview">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-2 border-b border-[#1F1F23] px-6 py-2.5">
          <h1 className="flex-1 font-display text-sm font-semibold">Interview</h1>
          <span className="text-xs text-muted-foreground" data-testid="formulator-interview-subtitle">
            {subtitle}
          </span>
        </div>

        <div ref={messagesRef} className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto px-6 py-5">
          {view.messages.map((message, index) => (
            <Message key={index} role={message.role}>
              {message.content}
            </Message>
          ))}
          {pending && <Message role="user">{pending.text}</Message>}

          {busy && !claudeFailure && (
            <div
              className="flex max-w-[540px] items-center gap-2.5 self-start rounded-xl border border-[#1F1F23] bg-[#111113] px-3.5 py-[11px]"
              role="status"
              data-testid="formulator-typing"
            >
              <TypingDots />
              <span className={cn("text-xs", TOOLBOX_TEXT.faint)}>{turn.activity ?? activityFallback}</span>
            </div>
          )}

          {claudeFailure && (
            <FormulatorNotice message={claudeFailure} testId="formulator-claude-error">
              <button type="button" className="font-medium text-link hover:underline" onClick={onRetryClaude}>
                Try again
              </button>
            </FormulatorNotice>
          )}

          {showDuplicates && (
            <DuplicatesPanel
              view={view}
              onImprove={onImproveInstead}
              onContinue={() => {
                setDuplicatesDismissed(true);
              }}
            />
          )}

          {quickPicks.length > 0 && !showDuplicates && (
            <div className="flex flex-wrap gap-2" data-testid="formulator-quick-picks">
              {quickPicks.map((pick) => (
                <button
                  key={pick}
                  type="button"
                  className="inline-flex h-[30px] items-center rounded-full border border-[#3F3F46] px-3 font-medium transition-colors hover:border-muted-foreground hover:bg-[#141417]"
                  onClick={() => {
                    send(pick);
                  }}
                  data-testid="formulator-quick-pick"
                >
                  {pick}
                </button>
              ))}
            </div>
          )}

          {belowBar && !busy && (
            <FormulatorNotice
              message={{
                text: "The draft does not meet the grooming bar yet, so nothing was written to ADO. Answer the remaining questions, then review it again.",
                setup: false,
              }}
              testId="formulator-below-bar"
            />
          )}

          {view.complete && !busy && (
            <button
              type="button"
              className="inline-flex h-[30px] items-center gap-1.5 self-start rounded-lg bg-primary px-[13px] text-[13px] font-semibold text-white transition-colors hover:bg-[#C91E38]"
              onClick={onReview}
              data-testid="formulator-review-button"
            >
              Review the draft
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          )}
        </div>

        {replyFailure && (
          <div className="px-6 pb-2">
            <FormulatorNotice message={replyFailure} testId="formulator-reply-error">
              {replyFailure.retry && retryText !== null && (
                <button
                  type="button"
                  className="font-medium text-link hover:underline disabled:pointer-events-none disabled:opacity-40"
                  onClick={() => {
                    send(retryText, true);
                  }}
                  disabled={busy}
                  data-testid="formulator-reply-retry"
                >
                  Try again
                </button>
              )}
            </FormulatorNotice>
          </div>
        )}

        <div className="flex items-center gap-2 border-t border-[#1F1F23] px-6 py-3">
          <input
            value={input}
            onChange={(event) => {
              setInput(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                send(input);
              }
            }}
            placeholder="Type an answer, or pick one above"
            aria-label="Your answer"
            disabled={view.status !== "active"}
            className="h-[42px] min-w-0 flex-1 rounded-[9px] border border-[#3F3F46] bg-[#111113] px-3.5 text-sm text-foreground outline-none transition-[border-color,box-shadow] focus:border-[#66A1FF] focus:ring-2 focus:ring-[#66A1FF]/25"
            data-testid="formulator-answer"
          />
          <button
            type="button"
            onClick={() => {
              send(input);
            }}
            disabled={!canType || input.trim() === ""}
            className="inline-flex h-[42px] items-center rounded-lg bg-primary px-[13px] text-[13px] font-semibold text-white transition-colors hover:bg-[#C91E38] disabled:pointer-events-none disabled:opacity-40"
            data-testid="formulator-send"
          >
            Send
          </button>
        </div>
      </div>

      <aside className="flex w-[400px] shrink-0 flex-col gap-3 overflow-y-auto border-l border-[#1F1F23] bg-[#0B0B0D] p-4">
        <DraftCard slotGuide={view.slotGuide} />
        <FindingsCard view={view} />
      </aside>
    </div>
  );
}
